import { NextRequest } from 'next/server';
import isStringDefined from '../is-string-defined';
import { Locales } from '@/types/enums';
import { getCookie, hasCookie } from 'cookies-next';
import { UserDTO } from '@/types/other';
import envPublic from '@/config/env.public';
import cookiesConstants from '@/constants/cookies.constants';
import { FetchClient } from '@/lib/node-fetch/fetchClient';
import { getAuthTokenCookie } from '../middleware.util';

function getUserBrowserLanguage(req: NextRequest): Locales {
  const acceptLanguage = req.headers.get('accept-language');

  if (acceptLanguage) {
    const languages = acceptLanguage.split(',').map(lang => lang.split(';')[0].trim());
    const primaryLanguage = languages[0];

    // Convert to ISO 639-1
    const languageCode = primaryLanguage.split('-')[0].toUpperCase();

    return getValidLocale(languageCode);
  } else {
    return Locales.EN;
  }
}

export function isLocaleValid(str: string | undefined | null): str is Locales {
  return isStringDefined(str) && Object.values(Locales).includes(str.toLowerCase() as Locales);
}

export function getValidLocale(str: string): Locales {
  return isLocaleValid(str) ? (str.toLowerCase() as Locales) : Locales.EN;
}

export async function getUserLanguage(req: NextRequest): Promise<Locales> {
  // Check for auth token cookie first
  if (hasCookie(cookiesConstants.COOKIES.AUTH_TOKENS.name, { req })) {
    try {
      const authTokens = getAuthTokenCookie(req);
      const access_token = authTokens?.accessToken;

      if (!access_token) {
        return Locales.EN;
      }

      // Set up headers for API request
      const headers = {
        Authorization: `Bearer ${access_token}`,
        Cookie: req.headers.get('cookie') ?? '',
        'x-forwarded-for': req.headers.get('x-forwarded-for') ?? '',
        'x-real-ip': req.headers.get('x-real-ip') ?? '',
      };

      // Fetch user data
      const fetchClient = new FetchClient(envPublic.NEXT_PUBLIC_BASE_URL);
      const user = await fetchClient.get<UserDTO | null>('/api/get-user', { headers });

      // Return user's locale if available, otherwise default to EN
      return user && 'id' in user ? getValidLocale(user.local || '') : Locales.EN;
    } catch (error) {
      return Locales.EN;
    }
  }

  // If no auth token, check for locale cookie and browser language
  const userLocale = getValidLocale(
    getCookie(cookiesConstants.COOKIES.NEXT_LOCALE.name, { req }) || ''
  );
  const browserLocale = getUserBrowserLanguage(req);

  // Use browser locale if user locale is EN, otherwise use user locale
  return userLocale === Locales.EN ? getValidLocale(browserLocale) : userLocale;
}
